import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { userRequest } from "../utils/utils";
import {
  IServiceRequest,
  IServiceResponse,
  IServicesListResponse,
} from "../types";

interface IServiceState {
  service: IServiceResponse | null;
  servicesList: IServicesListResponse | null;
  loading: boolean;
}

const initialState: IServiceState = {
  service: null,
  servicesList: null,
  loading: false,
};

export const createService = createAsyncThunk<
  IServiceResponse,
  IServiceRequest,
  { rejectValue: { error: string } }
>("services/createService", async (params, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.post(`dashboard/services/`, params);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getServicesList = createAsyncThunk<
  IServicesListResponse,
  number,
  { rejectValue: { error: string } }
>("services/getServicesList", async (page, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`/dashboard/services/?page=${page}`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getServiceDetails = createAsyncThunk<
  IServiceResponse,
  number,
  { rejectValue: { error: string } }
>("services/getServiceDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`/dashboard/services/${id}`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const updateService = createAsyncThunk<
  IServiceResponse,
  { id: number; formData: FormData },
  { rejectValue: { error: string } }
>("services/updateService", async ({ id, formData }, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.patch(
      `/dashboard/services/${id}/`,
      formData
    );
    return data;
  } catch (error) {
    console.log(error);
  }
});

export const deleteService = createAsyncThunk<
  { message: string },
  number,
  { rejectValue: { error: string } }
>("services/deleteService", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.delete(`dashboard/services/${id}/`);
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    resetService: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(createService.fulfilled, (state, action) => {
        state.service = action.payload;
        state.loading = false;
      })
      .addCase(createService.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getServicesList.fulfilled, (state, action) => {
        state.servicesList = action.payload;
        state.loading = false;
      })
      .addCase(getServiceDetails.fulfilled, (state, action) => {
        state.service = action.payload;
        state.loading = false;
      })

      .addCase(updateService.fulfilled, (state, action) => {
        state.service = action.payload;
      });
  },
});

export default serviceSlice.reducer;
